import {
  BOOKING_BRANDING_ERROR_CODES,
  DEFAULT_BOOKING_ACCENT,
  type LogoAction,
  MAX_LOGO_BYTES,
  normalizeAccentInput,
} from '@shared/core/booking-branding'
import type { BookingOnboardingDto } from '@shared/core/booking-onboarding'
import type { AxiosError } from 'axios'
import type { IntlShape } from 'react-intl'
import { ValidationError } from 'yup'
import {
  BOOKING_PAGE_NAME_MAX_LENGTH,
  bookingPageNameSchema,
  resolveBookingPageNamePrefill,
} from '../booking-page-name.schema'
import type {
  BrandingForm,
  BrandingLogo,
  BrandingMessage,
  BrandingSaveFailure,
  SavedBranding,
} from './booking-branding-card.interface'

export const BRANDING_SWATCHES = [
  DEFAULT_BOOKING_ACCENT,
  '#272727',
  '#0B5563',
  '#1F6F5B',
  '#B23A48',
  '#E07A2F',
  '#908EF2',
  '#F5E6C8',
] as const

export const SAVE_RETRY_DELAY_MS = 1_000

const RETRYABLE_SAVE_STATUSES = [502, 503, 504]
const ACCEPTED_LOGO_TYPES = ['image/png', 'image/jpeg']
const FILE_REJECTION_CODES: string[] = [
  BOOKING_BRANDING_ERROR_CODES.UNSUPPORTED_FILE_TYPE,
  BOOKING_BRANDING_ERROR_CODES.FILE_TOO_LARGE,
]
const SAVE_FAILED_MESSAGE: BrandingMessage = { id: 'settings.bookingBranding.saveFailed' }
const NAME_REJECTED_MESSAGE: BrandingMessage = { id: 'settings.bookingBranding.name.rejected' }
const NAME_TOO_LONG_MESSAGE: BrandingMessage = {
  id: 'settings.bookingBranding.name.tooLong',
  values: { max: String(BOOKING_PAGE_NAME_MAX_LENGTH) },
}
const NAME_ERROR_BY_RULE: Record<string, BrandingMessage> = {
  'not-blank-only': { id: 'settings.bookingBranding.name.blankOnly' },
  max: NAME_TOO_LONG_MESSAGE,
}

export function savedBrandingFromDto(dto: BookingOnboardingDto, accountName: string): SavedBranding {
  return {
    name: resolveBookingPageNamePrefill(dto.booking_page_name, accountName),
    accent: dto.accent_color ?? null,
    logoUrl: dto.logo_url ?? null,
  }
}

export function brandingFormFromSaved(saved: SavedBranding): BrandingForm {
  return {
    name: saved.name,
    accentInput: saved.accent ?? DEFAULT_BOOKING_ACCENT,
    logo: saved.logoUrl ? { kind: 'saved', url: saved.logoUrl } : null,
  }
}

export function isSameSavedBranding(left: SavedBranding, right: SavedBranding): boolean {
  return left.name === right.name && left.accent === right.accent && left.logoUrl === right.logoUrl
}

export function bookingPageNameError(name: string): BrandingMessage | null {
  try {
    bookingPageNameSchema.validateSync({ bookingPageName: name })
    return null
  } catch (error) {
    const rule = error instanceof ValidationError ? error.type : undefined
    return NAME_ERROR_BY_RULE[rule] ?? NAME_TOO_LONG_MESSAGE
  }
}

export function previewNameFor(name: string, accountName: string): string {
  return name.trim() || accountName
}

export function formatBrandingMessage(message: BrandingMessage, formatMessage: IntlShape['formatMessage']): string {
  return 'text' in message ? message.text : formatMessage({ id: message.id }, message.values)
}

export function parseAccentInput(accentInput: string) {
  return normalizeAccentInput(accentInput, { allowShort: true })
}

export function isBrandingDirty(form: BrandingForm, saved: SavedBranding): boolean {
  if (form.name !== saved.name) return true
  const accent = parseAccentInput(form.accentInput)
  if ('error' in accent || accent.value !== saved.accent) return true
  return logoActionFor(form.logo, saved) !== 'keep'
}

export function logoActionFor(logo: BrandingLogo, saved: SavedBranding): LogoAction {
  if (logo?.kind === 'new') return 'replace'
  if (!logo && saved.logoUrl) return 'remove'
  return 'keep'
}

export function buildBrandingFormData(accent: string | null, form: BrandingForm, saved: SavedBranding): FormData {
  const { logo, name } = form
  const formData = new FormData()
  formData.append('accent_color', accent ?? '')
  formData.append('logo_action', logoActionFor(logo, saved))
  if (logo?.kind === 'new') formData.append('logo', logo.file, logo.file.name)
  // Omitted when untouched: an unset name is prefilled with the account name, which must not be persisted.
  if (name !== saved.name) formData.append('booking_page_name', name.trim())
  return formData
}

export function isRetryableSaveError(error: unknown): boolean {
  const status = (error as AxiosError | undefined)?.response?.status
  // Only a cold logo function or a gateway blip clears on a second try; a 500 is a bug or misconfiguration.
  return status === undefined || RETRYABLE_SAVE_STATUSES.includes(status)
}

export function describeSaveFailure(error: unknown, fileName: string | null): BrandingSaveFailure {
  const data = (error as AxiosError<{ code?: string; message?: unknown }> | undefined)?.response?.data
  const code = data?.code
  if (code === BOOKING_BRANDING_ERROR_CODES.INVALID_BOOKING_PAGE_NAME) {
    const text = typeof data.message === 'string' ? data.message.trim() : ''
    return { target: 'name', message: text ? { text } : NAME_REJECTED_MESSAGE }
  }
  if (fileName && FILE_REJECTION_CODES.includes(code)) {
    return { target: 'logo', message: unsupportedFileMessage(fileName) }
  }
  if (fileName && code === BOOKING_BRANDING_ERROR_CODES.IMAGE_DIMENSIONS_TOO_LARGE) {
    return {
      target: 'logo',
      message: { id: 'settings.bookingBranding.logo.tooManyPixels', values: { file: fileName } },
    }
  }
  return { target: 'save', message: SAVE_FAILED_MESSAGE }
}

export function unsupportedFileMessage(fileName: string): BrandingMessage {
  return { id: 'settings.bookingBranding.logo.unsupportedFile', values: { file: fileName } }
}

export function isAcceptedLogoFile(file: File): boolean {
  return ACCEPTED_LOGO_TYPES.includes(file.type) && file.size <= MAX_LOGO_BYTES
}

export function logoSizeInKb(bytes: number): number {
  return Math.max(1, Math.round(bytes / 1024))
}

export function readImageSize(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight })
    image.onerror = () => reject(new Error('Image could not be decoded'))
    image.src = url
  })
}
